import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline,
  AutoFixHigh,
  DeleteOutline,
} from '@mui/icons-material';

import generateOpaqueToken from '@/util/generateOpaqueToken';
import ApiKeySecretField from './ApiKeySecretField';

const max_keys = 10;

function validateKeys(keys) {
  if (!Array.isArray(keys)) return true;
  return keys.some((k) => !k?.client_id || !k?.key);
}

export default function ApiKeyKeysEditor({
  value,
  onChange,
  disabled = false,
  allowNone = true,
  stacked = false,
}) {
  const keys = Array.isArray(value) ? value : [];
  const has_error = useMemo(() => validateKeys(keys), [keys]);

  const updateKey = (idx, patch) => {
    const next = [...keys];
    next[idx] = { ...next[idx], ...patch };
    onChange(next, validateKeys(next));
  };

  const addKey = () => {
    if (keys.length >= max_keys) return;
    const next = [...keys, { client_id: '', key: '' }];
    onChange(next, validateKeys(next));
  };

  const removeKey = (idx) => {
    const next = [...keys];
    next.splice(idx, 1);
    onChange(next, validateKeys(next));
  };

  const generateKey = (idx) => {
    updateKey(idx, { key: `sk-pml-${generateOpaqueToken()}` });
  };

  const renderKeyActions = (idx) => (
    <Stack direction='row' spacing={0} alignItems='center'>
      <Tooltip title='Generate API key'>
        <IconButton
          onClick={() => generateKey(idx)}
          disabled={disabled}
          aria-label='Generate API key'
        >
          <AutoFixHigh />
        </IconButton>
      </Tooltip>
      <IconButton
        onClick={() => removeKey(idx)}
        disabled={disabled}
        aria-label='Remove API key'
      >
        <DeleteOutline />
      </IconButton>
    </Stack>
  );

  return (
    <Box>
      <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 1 }}>
        <Typography variant='body1' fontWeight='medium'>
          API Keys
        </Typography>
        <Button
          variant='outlined'
          startIcon={<AddCircleOutline />}
          onClick={addKey}
          disabled={disabled || keys.length >= max_keys}
        >
          Add Key
        </Button>
        <Typography variant='body2' color='text.secondary'>
          {keys.length}/{max_keys}
        </Typography>
      </Stack>

      {keys.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {allowNone
            ? 'Add at least one API key, or switch Authorizer to None.'
            : 'Add at least one API key.'}
        </Typography>
      ) : null}

      {stacked ? (
        <Stack spacing={2}>
          {keys.map((k, idx) => (
            <Stack
              key={idx}
              direction='row'
              spacing={2}
              alignItems='center'
              sx={{ width: '100%' }}
            >
              <Box sx={{ flex: '0 0 41.666%', minWidth: 0 }}>
                <TextField
                  label='Client ID'
                  value={k.client_id || ''}
                  onChange={(e) =>
                    updateKey(idx, { client_id: e.target.value })
                  }
                  fullWidth
                  disabled={disabled}
                  error={!k.client_id}
                  required
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ApiKeySecretField
                  value={k.key || ''}
                  onChange={(e) => updateKey(idx, { key: e.target.value })}
                  disabled={disabled}
                  error={!k.key}
                  required
                />
              </Box>
              {renderKeyActions(idx)}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Grid container spacing={2} alignItems='center'>
          {keys.map((k, idx) => (
            <React.Fragment key={idx}>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  label='Client ID'
                  value={k.client_id || ''}
                  onChange={(e) =>
                    updateKey(idx, { client_id: e.target.value })
                  }
                  fullWidth
                  disabled={disabled}
                  error={!k.client_id}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <ApiKeySecretField
                  value={k.key || ''}
                  onChange={(e) => updateKey(idx, { key: e.target.value })}
                  disabled={disabled}
                  error={!k.key}
                  required
                />
              </Grid>
              <Grid
                size={{ xs: 12, md: 2 }}
                sx={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                {renderKeyActions(idx)}
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      )}

      {has_error ? (
        <Typography variant='body2' color='error' sx={{ mt: 1 }}>
          All API keys require both Client ID and Key.
        </Typography>
      ) : null}
    </Box>
  );
}
